/**
 * +specs: the Rogue's three specialisations (docs/design/classes/specs.md "Rogue"). Built like
 * warrior.ts: every base key 1–7 evolves on the same key, the signature mechanic lives in passives /
 * auras, spec-only abilities sit on Q.
 *
 *  - Shadowdancer (melee dps): Silhouettes (0–3). Umbral Step, Dodge, the Duskwalk opener and a landed
 *    Shade Jab leave a shadow clone where you stood; every clone repeats your next Umbral Finale a beat
 *    later at 50% (the sim's echo rule, combat/clones.ts) and fans with Shadow Fan.
 *  - Mirage (tank): Phantoms (0–3, 6 in Hall of Mirrors). Each adds dodge; when you dodge one shatters in
 *    your place (heals you, and the attacker's anger lands on you). Dodge or Sidestep opens a short
 *    Sidestep window: an attack avoided inside it is a Perfect Sidestep (a Phantom back, a heal, and the
 *    next Return Blow is free).
 *  - Venomwright (melee dps): three stacking toxins (Corrode, Numb, Blight); Catalyst detonates them and,
 *    with two or more kinds on the target, sets off Catalysis: a burst that spreads every kind to the pack.
 *
 * Names: original (checked against WoW's spell list; WoW names avoided are in classes/names.ts).
 * Numbers: tuned with `pnpm harness -- specs --specs shadowdancer,mirage,venomwright`.
 */
import type { AbilityDef, AuraDef, Condition, EffectSpec, SpecDef } from '@keystone/sim';
import { ABILITY, BASE_ABILITY } from '../../ids';
import { PLAYER_HP } from '../../balance';
import { OFF_GCD, aroundSelf, aroundTarget, interrupt } from '../common';
import { type SpecInfo, infoOf } from './types';

const RO = BASE_ABILITY.rogue;
const B = ABILITY.blademaster;
const W = ABILITY.warden;
const S = ABILITY.stormcaller;
const DODGE = ABILITY.common.dodge;

export const ROGUE_SPEC = { shadowdancer: 'shadowdancer', mirage: 'mirage', venomwright: 'venomwright' } as const;

/** Ability ids (key in the comment). */
export const SHADOWDANCER = {
  twinFang: 'shadowdancer.twin_fang', // 1 (Nick)
  umbralFinale: 'shadowdancer.umbral_finale', // 2 (Finale)
  duskwalk: 'shadowdancer.duskwalk', // 3 (Lurk)
  gloamstrike: 'shadowdancer.gloamstrike', // key 1 while Duskwalking
  umbralStep: 'shadowdancer.umbral_step', // 4 (Slip)
  shadowFan: 'shadowdancer.shadow_fan', // 5 (Bladefan)
  veilOfNight: 'shadowdancer.veil_of_night', // 6 (Ashveil)
  shadeJab: 'shadowdancer.shade_jab', // 7 (Throatjab)
  umbralConvergence: 'shadowdancer.umbral_convergence', // Q
  /** The clones' hits (logged under their own ids: meters, VFX, the Convergence bonus). */
  silhouetteEcho: 'shadowdancer.silhouette_echo',
  silhouetteFan: 'shadowdancer.silhouette_fan',
} as const;
export const SHADOWDANCER_AURA = {
  silhouettes: 'shadowdancer.silhouettes',
  nightglint: 'shadowdancer.nightglint',
  duskwalk: 'shadowdancer.duskwalk_buff', // out of combat (until broken)
  duskwalkFight: 'shadowdancer.duskwalk_fight', // in combat (4 s)
  gloamStun: 'shadowdancer.gloam_stun',
  veilOfNight: 'shadowdancer.veil_of_night_buff',
  nightsEdge: 'shadowdancer.nights_edge',
  convergence: 'shadowdancer.convergence_buff',
  footwork: 'shadowdancer.footwork', // hidden passive: Dodge / a landed Shade Jab leave a Silhouette; Nightglint
} as const;

export const MIRAGE = {
  falsestep: 'mirage.falsestep', // 1 (Nick)
  returnBlow: 'mirage.return_blow', // 2 (Finale)
  phantomLure: 'mirage.phantom_lure', // 3 (Lurk)
  sidestep: 'mirage.sidestep', // 4 (Slip)
  mirrorSpin: 'mirage.mirror_spin', // 5 (Bladefan)
  haze: 'mirage.haze', // 6 (Ashveil)
  mirrorJab: 'mirage.mirror_jab', // 7 (Throatjab)
  hallOfMirrors: 'mirage.hall_of_mirrors', // Q
} as const;
export const MIRAGE_AURA = {
  phantoms: 'mirage.phantoms',
  sidestepWindow: 'mirage.sidestep_window',
  counterpoise: 'mirage.counterpoise',
  poise: 'mirage.poise',
  haze: 'mirage.haze_debuff',
  hallOfMirrors: 'mirage.hall_of_mirrors_buff',
  veil: 'mirage.mirage_veil', // passive: tank threat (×4)
  weave: 'mirage.phantom_weave', // hidden passive: Dodge opens the Sidestep window
  lured: 'mirage.lured',
} as const;

export const VENOMWRIGHT = {
  venomEdge: 'venomwright.venom_edge', // 1 (Nick)
  catalyst: 'venomwright.catalyst', // 2 (Finale)
  creepingFume: 'venomwright.creeping_fume', // 3 (Lurk)
  paralyticOpener: 'venomwright.paralytic_opener', // key 1 while in the fumes
  causticRoll: 'venomwright.caustic_roll', // 4 (Slip)
  flaskToss: 'venomwright.flask_toss', // 5 (Bladefan)
  chokingGas: 'venomwright.choking_gas', // 6 (Ashveil)
  numbingJab: 'venomwright.numbing_jab', // 7 (Throatjab)
  plaguebloom: 'venomwright.plaguebloom', // Q
} as const;
export const VENOMWRIGHT_AURA = {
  corrode: 'venomwright.corrode',
  numb: 'venomwright.numb',
  blight: 'venomwright.blight',
  catalysis: 'venomwright.catalysis', // the reaction (a burst that spreads every toxin kind)
  fume: 'venomwright.fume_buff', // out of combat (until broken)
  fumeFight: 'venomwright.fume_fight', // in combat (4 s)
  paralysed: 'venomwright.paralysed',
  gasMask: 'venomwright.gas_mask',
  choked: 'venomwright.choked',
  gasSlow: 'venomwright.gas_slow',
  volatileFumes: 'venomwright.volatile_fumes', // proc: Catalysis readies a free Flask Toss
} as const;
/** Zone ids (renderer looks). */
export const ROGUE_ZONE = { haze: 'mirage.haze', causticPuddle: 'venomwright.caustic_puddle', chokingGas: 'venomwright.choking_gas', plaguebloom: 'venomwright.plaguebloom' } as const;

const IN_COMBAT: Condition = { type: 'selfInCombat' };
/** Key 3 out of combat: refund the in-combat cooldown down to the base Lurk's 2 s (the cooldown is paid before effects run). */
const OOC_STEALTH_REFUND = (abilityId: string): EffectSpec => ({ type: 'modifyCooldown', abilityId, deltaMs: -18_000 });
const stun = (id: string, name: string, ms: number): AuraDef => ({ id, name, kind: 'debuff', durationMs: ms, stacking: 'refresh', cc: 'stun' });
/** Rogue specs keep the base Tempo (25 per builder, 14/s) and Edge (0–5). */

// ============================================================================
// Shadowdancer (melee dps): Silhouettes
// ============================================================================

const SD = SHADOWDANCER;
const SDA = SHADOWDANCER_AURA;

/** The signature: shadow clones left where you stood. Each repeats your next Umbral Finale at 50%. */
const silhouettes: AuraDef = {
  id: SDA.silhouettes,
  name: 'Silhouettes',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'stack',
  maxStacks: 3,
  echoes: [
    { abilityIds: [SD.umbralFinale], mult: 0.5, echoAbilityId: SD.silhouetteEcho, delayMs: 220, stepMs: 160 },
    { abilityIds: [SD.shadowFan], mult: 0.5, echoAbilityId: SD.silhouetteFan, delayMs: 180, stepMs: 120, consume: false },
  ],
  ui: { stackCounterOn: [SD.umbralFinale, SD.shadowFan], glow: { abilityIds: [SD.umbralFinale], atStacks: 3 } },
};
const silhouette = (n = 1): EffectSpec => ({ type: 'applyAura', to: 'self', aura: silhouettes, stacks: n });

const nightglint: AuraDef = {
  id: SDA.nightglint,
  name: 'Nightglint',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  ui: { proc: true, glow: { abilityIds: [SD.twinFang] } },
};

const footwork: AuraDef = {
  id: SDA.footwork,
  name: 'Umbral Footwork',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [
    // Dodge leaves a Silhouette where you stood.
    { on: 'cast', abilityIds: [DODGE], effects: [silhouette()] },
    // A landed Shade Jab leaves one too.
    { on: 'interruptDealt', abilityIds: [SD.shadeJab], effects: [silhouette()] },
    // Nightglint: 20% (bad-luck protected) that a glint of shadow peels off (a Silhouette) and the next Twin Fang builds an extra Edge.
    { on: 'castResolved', abilityIds: [SD.twinFang], chance: 0.2, badLuck: { step: 0.06 }, effects: [{ type: 'applyAura', to: 'self', aura: nightglint }, silhouette()] },
  ],
};

const nightsEdge: AuraDef = {
  id: SDA.nightsEdge,
  name: "Night's Edge",
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'critChanceBonus', value: 1, abilityIds: [SD.umbralFinale] }],
  procs: [{ on: 'castResolved', abilityIds: [SD.umbralFinale], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [SD.umbralFinale] } },
};

const convergence: AuraDef = {
  id: SDA.convergence,
  name: 'Umbral Convergence',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  // The clones strike at full strength.
  mods: [{ stat: 'damageDonePct', value: 1, abilityIds: [SD.silhouetteEcho, SD.silhouetteFan] }],
  ui: { proc: true },
};

/** Duskwalk out of combat: like Lurk (until broken). In combat: 4 s. Both turn Twin Fang into Gloamstrike. */
const duskwalkBuff: AuraDef = {
  id: SDA.duskwalk,
  name: 'Duskwalking',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  flags: { stealth: true, removeInCombat: true, breakOnDamage: true },
  procs: [{ on: 'damageDealt', effects: [{ type: 'removeAura', to: 'self', auraId: SDA.duskwalk }] }],
  overrides: [{ abilityId: SD.twinFang, with: SD.gloamstrike }],
};
const duskwalkFight: AuraDef = {
  id: SDA.duskwalkFight,
  name: 'Duskwalking',
  kind: 'buff',
  durationMs: 4000,
  stacking: 'refresh',
  flags: { stealth: true },
  procs: [{ on: 'damageDealt', effects: [{ type: 'removeAura', to: 'self', auraId: SDA.duskwalkFight }] }],
  overrides: [{ abilityId: SD.twinFang, with: SD.gloamstrike }],
  ui: { proc: true, glow: { abilityIds: [SD.twinFang] } },
};

const twinFang: AbilityDef = {
  id: SD.twinFang,
  name: 'Twin Fang',
  school: 'physical',
  description: 'Two quick stabs. Costs 25 Tempo, builds 1 Edge; sometimes a Nightglint peels off: a Silhouette, and the next Twin Fang builds 2.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 25 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 0.94, variance: 0.05 } },
    { type: 'damage', school: 'physical', amount: { coef: 0.94, variance: 0.05 } },
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: SDA.nightglint }, then: [{ type: 'resource', to: 'self', resource: 'edge', amount: 1 }, { type: 'removeAura', to: 'self', auraId: SDA.nightglint }] },
  ],
  ui: {
    tags: ['builder'],
    color: '#a990ff',
    animAs: B.overpower,
    desc: 'Stab twice for {damage} Physical damage each. Costs 25 Tempo and builds 1 Edge. 20% chance of a Nightglint: a Silhouette peels off your shadow and your next Twin Fang builds 1 more Edge.',
    hint: 'Press 1 to build Edge: each Twin Fang is one step towards Umbral Finale.',
  },
};

const umbralFinale: AbilityDef = {
  id: SD.umbralFinale,
  name: 'Umbral Finale',
  school: 'physical',
  description: 'Spend 4 Edge: a finishing flurry. Every Silhouette repeats it a beat later at 50%, then fades.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  requiresFacing: true,
  cost: [{ resource: 'edge', amount: 4 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 4.4, variance: 0.05 } }],
  ui: {
    tags: ['spender'],
    color: '#7d5fe0',
    animAs: B.execute,
    desc: 'Spend 4 Edge: a finishing flurry for {damage} Physical damage. Each Silhouette you have repeats it a beat later for 50% (100% during Umbral Convergence), then fades.',
    hint: 'Press 2 at 4 Edge, best with 2–3 Silhouettes behind you.',
  },
};

const duskwalk: AbilityDef = {
  id: SD.duskwalk,
  name: 'Duskwalk',
  school: 'shadow',
  description: 'Step into the dusk, even mid-fight: Twin Fang becomes Gloamstrike. Out of combat it lasts until broken; in combat, 4 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20_000 },
  effects: [
    {
      type: 'conditional',
      cond: IN_COMBAT,
      then: [{ type: 'applyAura', to: 'self', aura: duskwalkFight }],
      // Out of combat it is the base Lurk (2 s): the 20 s cooldown is the price of vanishing mid-fight.
      else: [{ type: 'applyAura', to: 'self', aura: duskwalkBuff }, OOC_STEALTH_REFUND(SD.duskwalk)],
    },
  ],
  ui: {
    tags: ['cooldown'],
    color: '#5b4a8a',
    animAs: B.dieByTheSword,
    desc: 'Melt into the dusk. Out of combat enemies notice you only up close until you strike (2 sec cooldown, like Lurk); in combat you vanish for 4 sec (20 sec cooldown). Either way Twin Fang becomes Gloamstrike (2 sec stun, +2 Edge, +1 Silhouette). Off the global cooldown.',
    hint: 'Press 3 then 1: a stun, 2 Edge and a Silhouette, even mid-fight.',
  },
};

const gloamstrike: AbilityDef = {
  id: SD.gloamstrike,
  name: 'Gloamstrike',
  school: 'physical',
  description: 'From Duskwalk: strike from the gloom. Stuns for 2 s, builds 2 Edge and leaves a Silhouette.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  gain: [{ resource: 'edge', amount: 2 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.4, variance: 0.05 }, avoidable: false }, // +specs balance: 2.8 → 3.4 (the solo opener)
    { type: 'applyAura', aura: stun(SDA.gloamStun, 'Gloamstruck', 2000) },
    silhouette(),
    { type: 'removeAura', to: 'self', auraId: SDA.duskwalk },
    { type: 'removeAura', to: 'self', auraId: SDA.duskwalkFight },
  ],
  ui: { tags: ['builder'], color: '#8e7cc3', animAs: B.overpower, desc: 'From Duskwalk: strike from the gloom for {damage} Physical damage. Stuns the target for 2 sec, builds 2 Edge and leaves a Silhouette.' },
};

const umbralStep: AbilityDef = {
  id: SD.umbralStep,
  name: 'Umbral Step',
  school: 'shadow',
  description: 'Step through the shadows behind the target (up to 25 yd), leaving a Silhouette where you stood. 2 charges.',
  targeting: 'enemy',
  range: 25,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12_000, charges: 2 },
  requiresMobility: true,
  effects: [silhouette(), { type: 'dash', mode: 'behindTarget', distance: 25 }],
  ui: {
    tags: ['movement'],
    color: '#9c86e8',
    animAs: B.charge,
    desc: 'Step through the shadows to the back of an enemy up to 25 yd away. A Silhouette stays where you stood. 2 charges. Off the global cooldown.',
    hint: 'Press 4 to blink behind the target: each step leaves a clone for your Finale.',
  },
};

const shadowFan: AbilityDef = {
  id: SD.shadowFan,
  name: 'Shadow Fan',
  school: 'physical',
  description: 'Fan blades out at every enemy within 7 yd; every Silhouette fans with you at 50%. Costs 35 Tempo, builds 1 Edge.',
  targeting: 'none',
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 35 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', to: 'area', area: aroundSelf(7, 8), amount: { coef: 2.15, variance: 0.05 }, isAoE: true }],
  ui: {
    tags: ['aoe', 'builder'],
    color: '#b8a8ff',
    animAs: B.cleave,
    desc: 'Fan your blades at every enemy within 7 yd for {damage} Physical damage. Every Silhouette fans with you for 50% (they stay). Costs 35 Tempo, builds 1 Edge.',
    hint: 'Press 5 in a pack: your clones fan too.',
  },
};

const veilOfNight: AbilityDef = {
  id: SD.veilOfNight,
  name: 'Veil of Night',
  school: 'shadow',
  description: 'Wrap yourself in night: +50% dodge and 25% less damage for 4 s, enemies lose half their anger at you, a Silhouette stays behind and your next Umbral Finale crits.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 45_000 },
  effects: [
    {
      type: 'applyAura',
      to: 'self',
      aura: { id: SDA.veilOfNight, name: 'Veil of Night', kind: 'buff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'dodge', value: 0.5 }, { stat: 'damageTakenPct', value: -0.25 }] },
    },
    { type: 'threat', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 30 }, affects: 'enemies' }, multiply: 0.5 },
    { type: 'applyAura', to: 'self', aura: nightsEdge },
    silhouette(),
  ],
  ui: {
    tags: ['defensive', 'cooldown'],
    color: '#4b3d78',
    animAs: B.dieByTheSword,
    desc: "Wrap yourself in night for 4 sec: +50% dodge and 25% less damage taken. Nearby enemies lose half their threat on you, a Silhouette stays behind, and your next Umbral Finale within 12 sec is a guaranteed critical strike (Night's Edge). Off the global cooldown.",
    hint: 'Press 6 when hurt, or before a big Umbral Finale for the guaranteed crit.',
  },
};

const shadeJab: AbilityDef = {
  id: SD.shadeJab,
  name: 'Shade Jab',
  school: 'physical',
  description: 'Jab the target, interrupting its cast and locking that school for 3 s. A landed interrupt leaves a Silhouette.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.5 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: B.pummel, hint: 'Press 7 while an enemy is casting: stops the spell and leaves a Silhouette.' },
};

const umbralConvergence: AbilityDef = {
  id: SD.umbralConvergence,
  name: 'Umbral Convergence',
  school: 'shadow',
  description: 'Call 3 Silhouettes at once; for 8 s every Silhouette strikes at full strength.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  effects: [silhouette(3), { type: 'applyAura', to: 'self', aura: convergence }],
  ui: {
    tags: ['cooldown'],
    color: '#c9b8ff',
    animAs: B.bladestorm,
    desc: 'Three Silhouettes step out of your shadow at once, and for 8 sec every Silhouette echo hits for 100% instead of 50%. Off the global cooldown.',
    hint: 'Press Q, then Umbral Finale: four finales at once.',
  },
};

const silhouetteEcho: AbilityDef = {
  id: SD.silhouetteEcho,
  name: 'Silhouette Strike',
  school: 'physical',
  description: "A Silhouette repeats your Umbral Finale (50% of its damage; 100% during Umbral Convergence).",
  targeting: 'enemy',
  effects: [],
  ui: { color: '#a990ff', animAs: B.execute },
};
const silhouetteFan: AbilityDef = {
  id: SD.silhouetteFan,
  name: 'Silhouette Fan',
  school: 'physical',
  description: 'A Silhouette fans with your Shadow Fan (50% of its damage; 100% during Umbral Convergence).',
  targeting: 'none',
  effects: [],
  ui: { color: '#b8a8ff', animAs: B.cleave },
};

export const shadowdancer: SpecDef = {
  id: ROGUE_SPEC.shadowdancer,
  baseClassId: 'rogue',
  name: 'Shadowdancer',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [RO.nick]: SD.twinFang,
    [RO.finale]: SD.umbralFinale,
    [RO.lurk]: SD.duskwalk,
    [RO.slip]: SD.umbralStep,
    [RO.bladefan]: SD.shadowFan,
    [RO.ashveil]: SD.veilOfNight,
    [RO.throatjab]: SD.shadeJab,
  },
  extraAbilities: [{ key: 'Q', abilityId: SD.umbralConvergence }],
  abilities: [twinFang, umbralFinale, duskwalk, umbralStep, shadowFan, veilOfNight, shadeJab, umbralConvergence, gloamstrike, silhouetteEcho, silhouetteFan],
  passives: [footwork],
  auras: [silhouettes, nightglint, nightsEdge, convergence, duskwalkBuff, duskwalkFight],
  color: '#5b4a8a',
  ui: {
    tagline: 'Leaves shadow clones behind that repeat your finishers.',
    fantasy: 'Every step leaves a silhouette in the dark. Your clones mirror your finishers a beat later, so a well-timed combo lands three times over.',
    signature: 'Silhouettes (max 3): Umbral Step, Dodge, Nightglint, Gloamstrike and Shade Jab leave a clone where you stood; each clone repeats your next Umbral Finale at 50%, then fades.',
    weapon: 'twin_daggers',
    tint: '#5b4a8a',
    glow: '#a990ff',
  },
};

// ============================================================================
// Mirage (tank): Phantoms
// ============================================================================

const M = MIRAGE;
const MA = MIRAGE_AURA;

const hallBuff: AuraDef = {
  id: MA.hallOfMirrors,
  name: 'Hall of Mirrors',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'damageTakenPct', value: -0.1 }],
  ui: { proc: true },
};

/** The signature: up to 3 Phantoms (6 in Hall of Mirrors). Dodge per Phantom; a dodge shatters one in your place. */
const phantoms: AuraDef = {
  id: MA.phantoms,
  name: 'Phantoms',
  kind: 'buff',
  durationMs: 20_000,
  stacking: 'stack',
  maxStacks: 6,
  mods: [{ stat: 'dodge', value: 0.07 }],
  procs: [
    {
      on: 'avoided',
      hitTypes: ['dodge'],
      consumeStack: true,
      effects: [
        { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.025 } },
        // The attacker struck the Phantom: its anger lands on you.
        { type: 'threat', amount: 900 },
      ],
    },
  ],
  ui: { stackCounterOn: [M.returnBlow], glow: { abilityIds: [M.returnBlow], atStacks: 3 } },
};
const HALL: Condition = { type: 'selfHasAura', auraId: MA.hallOfMirrors };
/** +n Phantoms, up to 3 (6 inside Hall of Mirrors). */
const phantom = (n = 1): EffectSpec => ({
  type: 'conditional',
  cond: { type: 'or', any: [HALL, { type: 'not', cond: { type: 'selfHasAura', auraId: MA.phantoms, minStacks: 3 } }] },
  then: [{ type: 'applyAura', to: 'self', aura: phantoms, stacks: n }],
});

const counterpoise: AuraDef = {
  id: MA.counterpoise,
  name: 'Counterpoise',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  mods: [{ stat: 'resourceCostPct', value: -1, abilityIds: [M.returnBlow] }],
  procs: [{ on: 'castResolved', abilityIds: [M.returnBlow], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [M.returnBlow] } },
};

/** The Perfect Sidestep window: anything avoided inside it refunds a Phantom, heals and readies a free Return Blow. */
const sidestepWindow: AuraDef = {
  id: MA.sidestepWindow,
  name: 'Sidestep',
  kind: 'buff',
  durationMs: 600,
  stacking: 'refresh',
  mods: [{ stat: 'dodge', value: 1 }],
  procs: [
    {
      on: 'avoided',
      hitTypes: ['dodge'],
      effects: [
        { type: 'removeAura', to: 'self', auraId: MA.sidestepWindow },
        { type: 'applyAura', to: 'self', aura: phantoms },
        { type: 'applyAura', to: 'self', aura: counterpoise },
        { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.04 } },
      ],
    },
  ],
  ui: { proc: true },
};
const openWindow = (ms: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura: sidestepWindow, durationMs: ms });

const mirageVeil: AuraDef = {
  id: MA.veil,
  name: 'Mirage Veil',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  mods: [{ stat: 'threatPct', value: 3 }],
  flags: { hidden: true, persistThroughDeath: true },
};
const phantomWeave: AuraDef = {
  id: MA.weave,
  name: 'Phantom Weave',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [
    // Dodge opens the Sidestep window: time it into a swing.
    { on: 'cast', abilityIds: [DODGE], effects: [openWindow(600)] },
    // A landed Mirror Jab leaves a Phantom.
    { on: 'interruptDealt', abilityIds: [M.mirrorJab], effects: [phantom()] },
  ],
};

const poise: AuraDef = { id: MA.poise, name: 'Poise', kind: 'buff', durationMs: 5000, stacking: 'refresh', mods: [{ stat: 'dodge', value: 0.12 }] };

const falsestep: AbilityDef = {
  id: M.falsestep,
  name: 'Falsestep',
  school: 'physical',
  description: 'A feint and a real cut. High threat; costs 20 Tempo, builds 1 Edge and a Phantom.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 20 }],
  gain: [{ resource: 'edge', amount: 1 }],
  flatThreat: 900,
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 1.6, variance: 0.05 } }, phantom()],
  ui: {
    tags: ['builder'],
    color: '#9fe3f0',
    animAs: B.mortalStrike,
    desc: 'Feint, then cut for {damage} Physical damage with high threat. Costs 20 Tempo, builds 1 Edge and leaves a Phantom (up to 3).',
    hint: 'Press 1 to keep 3 Phantoms around you and the enemy angry at you.',
  },
};

const returnBlow: AbilityDef = {
  id: M.returnBlow,
  name: 'Return Blow',
  school: 'physical',
  description: 'Spend 3 Edge: counter every enemy within 8 yd, harder per Phantom; heal and gain Poise.',
  targeting: 'none',
  gcdMs: 1000,
  cost: [{ resource: 'edge', amount: 3 }],
  flatThreat: 600,
  effects: [
    { type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 1.9, variance: 0.05, perStack: { auraId: MA.phantoms, on: 'self', mult: 0.25 } }, threatMult: 2, isAoE: true },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.03, perStack: { auraId: MA.phantoms, on: 'self', mult: 0.5 } } },
    { type: 'applyAura', to: 'self', aura: poise },
  ],
  ui: {
    tags: ['spender', 'aoe', 'heal'],
    color: '#c9f4ff',
    animAs: W.riposte,
    desc: 'Spend 3 Edge: counter every enemy within 8 yd for {damage} Physical damage (+25% per Phantom) with double threat, heal 3% of your maximum health (+50% per Phantom) and gain Poise (+12% dodge, 5 sec). Free after a Perfect Sidestep.',
    hint: 'Press 2 at 3 Edge, harder the more Phantoms you have.',
  },
};

const PHANTOM_LURE_COEF = 3.0;
const phantomLure: AbilityDef = {
  id: M.phantomLure,
  name: 'Phantom Lure',
  school: 'shadow',
  description: 'Send a Phantom to strike and taunt an enemy up to 30 yd away: it attacks you for 3 s. Leaves a Phantom.',
  targeting: 'enemy',
  range: 30,
  gcdMs: 1000,
  cooldown: { durationMs: 8000 },
  flatThreat: 1500,
  // +specs balance: the Phantom strikes as it taunts (the Mirage's opener: it has no Lurk).
  effects: [{ type: 'damage', school: 'shadow', amount: { coef: PHANTOM_LURE_COEF, variance: 0.05 } }, { type: 'taunt', durationMs: 3000 }, { type: 'applyAura', aura: { id: MA.lured, name: 'Lured', kind: 'debuff', durationMs: 3000, stacking: 'refresh' } }, phantom()],
  ui: {
    tags: ['taunt'],
    color: '#7fd3e6',
    animAs: W.taunt,
    desc: 'A Phantom flickers in front of an enemy up to 30 yd away, strikes it for {damage} Shadow damage and taunts it: it must attack you for 3 sec. You gain a Phantom.',
    hint: 'Press 3 on anything not hitting you (and to top up Phantoms).',
  },
};

const sidestep: AbilityDef = {
  id: M.sidestep,
  name: 'Sidestep',
  school: 'physical',
  description: "Slip behind the target (up to 25 yd): +1 Edge, a Phantom, and a 1 s Sidestep window (+100% dodge; avoiding a hit in it is a Perfect Sidestep).",
  targeting: 'enemy',
  range: 25,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12_000 },
  requiresMobility: true,
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [phantom(), openWindow(1000), { type: 'dash', mode: 'behindTarget', distance: 25 }],
  ui: {
    tags: ['movement', 'defensive'],
    color: '#a8ecff',
    animAs: B.charge,
    desc: "Slip to the back of an enemy up to 25 yd away, leaving a Phantom and building 1 Edge. For 1 sec you dodge everything: an attack avoided then is a Perfect Sidestep (a Phantom back, heal 4%, your next Return Blow costs nothing). Your Dodge opens the same window for 0.6 sec. Off the global cooldown.",
    hint: 'Press 4 (or Dodge) just before a big swing: a Perfect Sidestep readies a free Return Blow.',
  },
};

const mirrorSpin: AbilityDef = {
  id: M.mirrorSpin,
  name: 'Mirror Spin',
  school: 'physical',
  description: 'Spin through every enemy within 7 yd with triple threat. Costs 30 Tempo, builds 1 Edge.',
  targeting: 'none',
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 30 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', to: 'area', area: aroundSelf(7, 10), amount: { coef: 1.2, variance: 0.05 }, threatMult: 3, isAoE: true }],
  ui: { tags: ['aoe', 'builder'], color: '#bfeffa', animAs: B.cleave, desc: 'Spin through every enemy within 7 yd for {damage} Physical damage with triple threat. Costs 30 Tempo, builds 1 Edge.', hint: 'Press 5 in a pack: holds every enemy on you.' },
};

const haze: AbilityDef = {
  id: M.haze,
  name: 'Haze',
  school: 'shadow',
  description: 'Fill the ground around you with shimmering haze for 8 s: enemies inside deal 25% less damage.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 45_000 },
  effects: [
    {
      type: 'zone',
      zoneId: ROGUE_ZONE.haze,
      anchor: 'caster',
      shape: { type: 'circle', radius: 8 },
      durationMs: 8000,
      affects: 'enemies',
      insideAura: { id: MA.haze, name: 'Hazed', kind: 'debuff', durationMs: 0, stacking: 'refresh', mods: [{ stat: 'damageDonePct', value: -0.25 }] },
    },
  ],
  ui: {
    tags: ['defensive', 'majorDefensive'],
    color: '#8fd7e8',
    animAs: B.dieByTheSword,
    desc: 'Fill an 8 yd circle around you with shimmering haze for 8 sec: enemies standing in it deal 25% less damage. Off the global cooldown.',
    hint: 'Press 6 before a big pull or a boss swing.',
  },
};

const mirrorJab: AbilityDef = {
  id: M.mirrorJab,
  name: 'Mirror Jab',
  school: 'physical',
  description: 'Jab the target, interrupting its cast and locking that school for 3 s. A landed interrupt leaves a Phantom.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.5 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: B.pummel, hint: 'Press 7 while an enemy is casting.' },
};

const hallOfMirrors: AbilityDef = {
  id: M.hallOfMirrors,
  name: 'Hall of Mirrors',
  school: 'shadow',
  description: 'Six Phantoms at once (cap 6 for 10 s), 10% less damage taken, and every enemy within 10 yd is taunted.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90_000 },
  effects: [
    { type: 'applyAura', to: 'self', aura: hallBuff },
    { type: 'applyAura', to: 'self', aura: phantoms, stacks: 6 },
    { type: 'taunt', to: 'area', area: aroundSelf(10, 12), durationMs: 3000 },
    { type: 'threat', to: 'area', area: aroundSelf(10, 12), amount: 2500 },
  ],
  ui: {
    tags: ['cooldown', 'taunt', 'defensive'],
    color: '#e6fbff',
    animAs: B.bladestorm,
    desc: 'Mirrors rise around you: you hold up to 6 Phantoms for 10 sec and gain all 6 now, take 10% less damage, and every enemy within 10 yd is taunted. Off the global cooldown.',
    hint: 'Press Q on a big pull: six Phantoms soak the blows.',
  },
};

export const mirage: SpecDef = {
  id: ROGUE_SPEC.mirage,
  baseClassId: 'rogue',
  name: 'Mirage',
  role: 'tank',
  unlockLevel: 10,
  evolutions: {
    [RO.nick]: M.falsestep,
    [RO.finale]: M.returnBlow,
    [RO.lurk]: M.phantomLure,
    [RO.slip]: M.sidestep,
    [RO.bladefan]: M.mirrorSpin,
    [RO.ashveil]: M.haze,
    [RO.throatjab]: M.mirrorJab,
  },
  extraAbilities: [{ key: 'Q', abilityId: M.hallOfMirrors }],
  abilities: [falsestep, returnBlow, phantomLure, sidestep, mirrorSpin, haze, mirrorJab, hallOfMirrors],
  passives: [mirageVeil, phantomWeave],
  auras: [phantoms, sidestepWindow, counterpoise, poise, hallBuff],
  // An evasion tank: high dodge, some parry, light armour, tank health.
  baseStats: { armor: 0.24, dodge: 0.16, parry: 0.08, block: 0 },
  maxHp: PLAYER_HP.tank,
  color: '#7fd3e6',
  ui: {
    tagline: 'An evasion tank: enemies swing at illusions while you counter.',
    fantasy: 'You never take the hit. Phantoms pull aggro and soak blows while you weave between them, dodging and countering. Tanking as a dance.',
    signature: 'Phantoms (0–3): each adds 7% dodge and shatters in your place when you dodge (healing 2.5%); a perfectly timed Dodge or Sidestep refunds one and makes Return Blow free.',
    weapon: 'twin_daggers',
    tint: '#7fd3e6',
    glow: '#c9f4ff',
  },
};

// ============================================================================
// Venomwright (melee dps): Toxins
// ============================================================================

const V = VENOMWRIGHT;
const VA = VENOMWRIGHT_AURA;
const TOXINS = [VA.corrode, VA.numb, VA.blight] as const;

const corrode: AuraDef = {
  id: VA.corrode,
  name: 'Corrode',
  kind: 'debuff',
  durationMs: 12_000,
  stacking: 'stack',
  maxStacks: 5,
  dispelType: 'poison',
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.2 }, avoidable: false }] },
  mods: [{ stat: 'armor', value: -0.03 }],
  ui: { stackCounterOn: [V.venomEdge, V.catalyst] },
};
const numb: AuraDef = {
  id: VA.numb,
  name: 'Numb',
  kind: 'debuff',
  durationMs: 10_000,
  stacking: 'stack',
  maxStacks: 3,
  dispelType: 'poison',
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.22 }, avoidable: false }] },
  mods: [
    { stat: 'damageDonePct', value: -0.05 },
    { stat: 'moveSpeedPct', value: -0.07 },
  ],
};
const blight: AuraDef = {
  id: VA.blight,
  name: 'Blight',
  kind: 'debuff',
  durationMs: 12_000,
  stacking: 'stack',
  maxStacks: 3,
  dispelType: 'poison',
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.26 }, avoidable: false }] },
  ui: { stackCounterOn: [V.flaskToss] },
};
const TOXIN_DEF: Readonly<Record<string, AuraDef>> = { [VA.corrode]: corrode, [VA.numb]: numb, [VA.blight]: blight };
const toxin = (def: AuraDef, n = 1, to: 'target' | 'area' = 'target', area = aroundTarget(6, 8)): EffectSpec =>
  to === 'area' ? { type: 'applyAura', to: 'area', area, aura: def, stacks: n } : { type: 'applyAura', aura: def, stacks: n };
const has = (id: string): Condition => ({ type: 'targetHasAura', auraId: id, fromSelf: true });
/** Two or more toxin kinds on the target: Catalyst sets off Catalysis. */
export const TWO_TOXINS: Condition = { type: 'or', any: [{ type: 'and', all: [has(VA.corrode), has(VA.numb)] }, { type: 'and', all: [has(VA.corrode), has(VA.blight)] }, { type: 'and', all: [has(VA.numb), has(VA.blight)] }] };

const volatileFumes: AuraDef = {
  id: VA.volatileFumes,
  name: 'Volatile Fumes',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'resourceCostPct', value: -1, abilityIds: [V.flaskToss] }],
  procs: [{ on: 'castResolved', abilityIds: [V.flaskToss], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [V.flaskToss] } },
};

/** The reaction: a toxic burst around the target, and 2 stacks of every kind it carries spread to the pack. */
const catalysis: AuraDef = {
  id: VA.catalysis,
  name: 'Catalysis',
  kind: 'debuff',
  durationMs: 600,
  stacking: 'refresh',
  onApply: [
    { type: 'damage', school: 'nature', to: 'area', area: aroundTarget(8, 8), amount: { coef: 0.9, variance: 0.05 }, isAoE: true },
    ...TOXINS.map(
      (id): EffectSpec => ({ type: 'conditional', cond: has(id), then: [{ type: 'applyAura', to: 'area', area: { ...aroundTarget(8, 8), excludePrimary: true }, aura: TOXIN_DEF[id]!, stacks: 2 }] }),
    ),
    // The fumes linger: the next Flask Toss costs nothing.
    { type: 'applyAura', to: 'self', aura: volatileFumes },
  ],
  ui: { proc: true },
};

const fumeBuff: AuraDef = {
  id: VA.fume,
  name: 'Creeping Fume',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  flags: { stealth: true, removeInCombat: true, breakOnDamage: true },
  procs: [{ on: 'damageDealt', effects: [{ type: 'removeAura', to: 'self', auraId: VA.fume }] }],
  overrides: [{ abilityId: V.venomEdge, with: V.paralyticOpener }],
};
const fumeFight: AuraDef = {
  id: VA.fumeFight,
  name: 'Creeping Fume',
  kind: 'buff',
  durationMs: 4000,
  stacking: 'refresh',
  flags: { stealth: true },
  procs: [{ on: 'damageDealt', effects: [{ type: 'removeAura', to: 'self', auraId: VA.fumeFight }] }],
  overrides: [{ abilityId: V.venomEdge, with: V.paralyticOpener }],
  ui: { proc: true, glow: { abilityIds: [V.venomEdge] } },
};

const venomEdge: AbilityDef = {
  id: V.venomEdge,
  name: 'Venom Edge',
  school: 'physical',
  description: 'A coated cut that adds a stack of Corrode (up to 5). Costs 25 Tempo, builds 1 Edge.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 25 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 1.6, variance: 0.05 } }, toxin(corrode)],
  ui: {
    tags: ['builder'],
    color: '#8fd45a',
    animAs: B.rend,
    desc: 'A coated cut for {damage} Physical damage that adds a stack of Corrode (Nature damage every 2 sec for 12 sec, −3% armour per stack, up to 5). Costs 25 Tempo, builds 1 Edge.',
    hint: 'Press 1 to stack Corrode and build Edge.',
  },
};

const catalyst: AbilityDef = {
  id: V.catalyst,
  name: 'Catalyst',
  school: 'nature',
  description: 'Spend 4 Edge: detonate every toxin on the target, more per stack. With two or more kinds on it, Catalysis spreads them to the pack.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  requiresFacing: true,
  cost: [{ resource: 'edge', amount: 4 }],
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 2.55, variance: 0.05 } },
    { type: 'damage', school: 'nature', if: has(VA.corrode), amount: { coef: 0.52, perStack: { auraId: VA.corrode, on: 'target', mult: 1 } } },
    { type: 'damage', school: 'nature', if: has(VA.numb), amount: { coef: 0.6, perStack: { auraId: VA.numb, on: 'target', mult: 1 } } },
    { type: 'damage', school: 'nature', if: has(VA.blight), amount: { coef: 0.62, perStack: { auraId: VA.blight, on: 'target', mult: 1 } } },
    { type: 'conditional', cond: TWO_TOXINS, then: [{ type: 'applyAura', aura: catalysis }] },
    ...TOXINS.map((id): EffectSpec => ({ type: 'removeAura', auraId: id })),
  ],
  ui: {
    tags: ['spender'],
    color: '#b8ff7a',
    animAs: B.colossusSmash,
    desc: 'Spend 4 Edge: detonate every toxin on the target for {damage} Nature damage, plus a burst per toxin kind that grows with its stacks; the toxins are spent. With two or more kinds on the target it sets off Catalysis: a toxic burst on every enemy within 8 yd, and 2 stacks of each kind spread to them. Catalysis also leaves Volatile Fumes: your next Flask Toss within 10 sec costs no Tempo.',
    hint: 'Press 2 at 4 Edge with two toxin kinds on the target: the reaction spreads.',
  },
};

const creepingFume: AbilityDef = {
  id: V.creepingFume,
  name: 'Creeping Fume',
  school: 'nature',
  description: 'Vanish into a drift of fumes, even mid-fight: Venom Edge becomes Paralytic Opener. Out of combat it lasts until broken; in combat, 4 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20_000 },
  // Out of combat it is the base Lurk (2 s): the 20 s cooldown is the price of vanishing mid-fight.
  effects: [{ type: 'conditional', cond: IN_COMBAT, then: [{ type: 'applyAura', to: 'self', aura: fumeFight }], else: [{ type: 'applyAura', to: 'self', aura: fumeBuff }, OOC_STEALTH_REFUND(V.creepingFume)] }],
  ui: {
    tags: ['cooldown'],
    color: '#6f9e4a',
    animAs: B.dieByTheSword,
    desc: 'Vanish into a drift of fumes. Out of combat enemies notice you only up close until you strike (2 sec cooldown, like Lurk); in combat you fade for 4 sec (20 sec cooldown). Either way Venom Edge becomes Paralytic Opener (2 sec stun, 2 Numb, +2 Edge). Off the global cooldown.',
    hint: 'Press 3 then 1: a stun, two stacks of Numb and 2 Edge, even mid-fight.',
  },
};

const paralyticOpener: AbilityDef = {
  id: V.paralyticOpener,
  name: 'Paralytic Opener',
  school: 'physical',
  description: 'From Creeping Fume: a paralysing needle that carries every toxin. Stuns for 2 s, adds 2 Numb, a Corrode and a Blight, builds 2 Edge.',
  targeting: 'enemy',
  melee: true,
  gcdMs: 1000,
  gain: [{ resource: 'edge', amount: 2 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.4, variance: 0.05 }, avoidable: false }, // +specs balance: 2.6 → 3.4 (the solo opener)
    { type: 'applyAura', aura: stun(VA.paralysed, 'Paralysed', 2000) },
    toxin(numb, 2),
    toxin(corrode),
    toxin(blight), // +specs balance: all three kinds from the first press (a field mob dies before a vial would land)
    { type: 'removeAura', to: 'self', auraId: VA.fume },
    { type: 'removeAura', to: 'self', auraId: VA.fumeFight },
  ],
  ui: { tags: ['builder'], color: '#a6d96a', animAs: B.overpower, desc: 'From Creeping Fume: a paralysing needle for {damage} Physical damage that carries every toxin. Stuns the target for 2 sec, adds 2 stacks of Numb (−5% damage dealt each), a Corrode and a Blight, builds 2 Edge.' },
};

const causticRoll: AbilityDef = {
  id: V.causticRoll,
  name: 'Caustic Roll',
  school: 'nature',
  description: 'Roll behind the target (up to 25 yd), smashing a vial where you stood: an acid puddle that burns and Blights enemies for 6 s. +1 Edge.',
  targeting: 'enemy',
  range: 25,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  requiresMobility: true,
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [
    {
      type: 'zone',
      zoneId: ROGUE_ZONE.causticPuddle,
      anchor: 'caster',
      shape: { type: 'circle', radius: 5 },
      durationMs: 6000,
      affects: 'enemies',
      tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.4, variance: 0.05 }, isAoE: true }, { type: 'applyAura', aura: blight }] },
    },
    { type: 'dash', mode: 'behindTarget', distance: 25 },
  ],
  ui: {
    tags: ['movement', 'aoe'],
    color: '#9be05a',
    animAs: B.charge,
    desc: 'Roll to the back of an enemy up to 25 yd away and smash a vial where you stood: for 6 sec enemies within 5 yd of it take {damage} Nature damage every second and gain a stack of Blight. Builds 1 Edge. Off the global cooldown.',
    hint: 'Press 4 on cooldown: a puddle of acid and Blight under the fight.',
  },
};

const flaskToss: AbilityDef = {
  id: V.flaskToss,
  name: 'Flask Toss',
  school: 'nature',
  description: 'Lob a vial up to 30 yd: damage and a stack of Blight on every enemy within 6 yd of the target. Costs 35 Tempo, builds 1 Edge.',
  targeting: 'enemy',
  range: 30,
  gcdMs: 1000,
  cost: [{ resource: 'tempo', amount: 35 }],
  gain: [{ resource: 'edge', amount: 1 }],
  effects: [
    { type: 'damage', school: 'nature', to: 'area', area: aroundTarget(6, 8), amount: { coef: 0.72, variance: 0.05 }, isAoE: true },
    toxin(blight, 1, 'area'),
  ],
  ui: {
    tags: ['aoe', 'builder'],
    color: '#b8ff7a',
    animAs: S.flameShock,
    desc: 'Lob a vial at an enemy up to 30 yd away: {damage} Nature damage and a stack of Blight (Nature damage every 2 sec for 12 sec, up to 3) on every enemy within 6 yd. Costs 35 Tempo, builds 1 Edge.',
    hint: 'Press 5 to Blight the whole pack (and the boss, for a second toxin kind).',
  },
};

const chokingGas: AbilityDef = {
  id: V.chokingGas,
  name: 'Choking Gas',
  school: 'nature',
  description: 'Burst a gas cloud around you for 6 s: enemies inside are slowed 50%, silenced for 2 s once and Numbed; you slip through it, dodging and taking less damage.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 45_000 },
  effects: [
    {
      type: 'zone',
      zoneId: ROGUE_ZONE.chokingGas,
      anchor: 'caster',
      shape: { type: 'circle', radius: 8 },
      durationMs: 6000,
      affects: 'enemies',
      insideAura: { id: VA.gasSlow, name: 'Choking', kind: 'debuff', durationMs: 0, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: -0.5 }] },
    },
    { type: 'applyAura', to: 'area', area: aroundSelf(8, 12), aura: { id: VA.choked, name: 'Choked', kind: 'debuff', durationMs: 2000, stacking: 'refresh', cc: 'silence' } },
    toxin(numb, 1, 'area', aroundSelf(8, 12)),
    {
      type: 'applyAura',
      to: 'self',
      aura: { id: VA.gasMask, name: 'Gas Mask', kind: 'buff', durationMs: 6000, stacking: 'refresh', mods: [{ stat: 'dodge', value: 0.4 }, { stat: 'damageTakenPct', value: -0.25 }] },
    },
  ],
  ui: {
    tags: ['defensive', 'aoe'],
    color: '#7fb24a',
    animAs: W.consecratedGround,
    desc: 'Burst a cloud of choking gas around you for 6 sec: enemies in it are slowed by 50%, silenced for 2 sec and gain a stack of Numb. You slip through it with +40% dodge and 25% less damage taken. Off the global cooldown.',
    hint: 'Press 6 when hurt or when casters gather.',
  },
};

const numbingJab: AbilityDef = {
  id: V.numbingJab,
  name: 'Numbing Jab',
  school: 'physical',
  description: 'Jab a numbing needle into the target, interrupting its cast (3 s school lockout) and adding a stack of Numb.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.5 } }, interrupt(3000), toxin(numb)],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: B.pummel, hint: 'Press 7 while an enemy is casting: it also adds Numb (a third toxin kind).' },
};

const plaguebloom: AbilityDef = {
  id: V.plaguebloom,
  name: 'Plaguebloom',
  school: 'nature',
  description: 'Burst a blooming gas cloud on the target for 8 s: every 2 s it adds a stack of every toxin to each enemy inside.',
  targeting: 'enemy',
  range: 30,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 45_000 },
  effects: [
    {
      type: 'zone',
      zoneId: ROGUE_ZONE.plaguebloom,
      anchor: 'target',
      shape: { type: 'circle', radius: 8 },
      durationMs: 8000,
      affects: 'enemies',
      tick: { intervalMs: 2000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.3, variance: 0.05 }, isAoE: true }, toxin(corrode), toxin(numb), toxin(blight)] },
    },
    toxin(corrode),
    toxin(numb),
    toxin(blight),
  ],
  ui: {
    tags: ['cooldown', 'aoe'],
    color: '#c6ff5a',
    animAs: S.earthquake,
    desc: 'A gas cloud blooms on an enemy up to 30 yd away for 8 sec: it adds a stack of Corrode, Numb and Blight to the target now, and every 2 sec to each enemy inside (with {damage} Nature damage). Off the global cooldown.',
    hint: 'Press Q on a pack, then Catalyst: every kind of toxin, everywhere.',
  },
};

export const venomwright: SpecDef = {
  id: ROGUE_SPEC.venomwright,
  baseClassId: 'rogue',
  name: 'Venomwright',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [RO.nick]: V.venomEdge,
    [RO.finale]: V.catalyst,
    [RO.lurk]: V.creepingFume,
    [RO.slip]: V.causticRoll,
    [RO.bladefan]: V.flaskToss,
    [RO.ashveil]: V.chokingGas,
    [RO.throatjab]: V.numbingJab,
  },
  extraAbilities: [{ key: 'Q', abilityId: V.plaguebloom }],
  abilities: [venomEdge, catalyst, creepingFume, causticRoll, flaskToss, chokingGas, numbingJab, plaguebloom, paralyticOpener],
  auras: [corrode, numb, blight, catalysis, volatileFumes, fumeBuff, fumeFight],
  color: '#5fae3c',
  ui: {
    tagline: 'An alchemist who brews stacking toxins, then detonates them.',
    fantasy: 'Vials, gas and green fire. You coat a whole pack in layered poisons, then light the match and watch it chain.',
    signature: 'Toxins: Corrode, Numb and Blight stack on targets; Catalyst detonates them, and with two or more kinds on the target Catalysis bursts and spreads every kind to the pack.',
    weapon: 'vials',
    tint: '#5fae3c',
    glow: '#b8ff7a',
  },
};

/** Implemented Rogue specs (Tower Warden, harness, bots). */
export const ROGUE_SPECS: SpecDef[] = [shadowdancer, mirage, venomwright];

/** Preview info for all three (the launch set is bold in advancement.md). */
export const ROGUE_SPEC_INFO: SpecInfo[] = [infoOf(shadowdancer, true), infoOf(mirage, true), infoOf(venomwright, false)];
